<script>

import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import C3Chart from '@/components/shared/chart/C3Chart.vue'
import Moment from 'moment-timezone'
import * as d3 from 'd3'
import { cloneDeep, defaultsDeep, get, forEach, snakeCase, min, max, toNumber } from 'lodash'
import $ from 'jquery'

export default {
  name: 'TemperatureChart',
  extends: C3Chart,
  mixins: [CurrentUserMixin],
  props: {
    height: {
      type: Number,
      default: 300
    },
    showDeliveries: {
      type: Boolean,
      default: false
    },
    initialColumnVisibility: {
      type: Object
    }
  },
  data () {
    return {
      /**
       * Desired width of the chart in pixels
       *
       * @var int
       */
      width: 0,

      /**
       * Holds a list of colors associated with each kind of data represented on
       * this chart.
       *
       * @var {Object}
       */
      colors: {
        coolerAir: '#1e941e',
        freezerAir: '#1f77b4',
        coolerSetPoint: '#FFA35C',
        freezerSetPoint: '#E7BF0A',
        danger: '#d9534f'
      },

      productColors: {
        cooler: '#29de29',
        freezer: '#44afed'
      },

      /**
       * used internally to keep track of the x axis ticks so that we can show
       * the date and time when we cross from one date to the next.
       *
       * @var {Moment}
       */
      previousTickTimestamp: Moment().unix(),

      /**
       * When we generate data if there are forecasts we store the confidence
       * window data here and render them out as an area using the underlying d3
       * engine.
       *
       * @var {Object}
       */
      confidenceWindows: {}
    }
  },
  methods: {
    /**
     * If the customer context changes then regenerate the chart in order to up
     * date axis lables, etc... (called by the CurrentUserMixin)
     *
     * @return undefined
     */
    customerContextChangeHandler () {
      this.regenerate()
    },

    /**
     * Renders the tick time as either just the hour, minute, period (am/pm) or
     * includes the data (if the previous time was on a different date)
     *
     * @return string
     */
    formatTick (timestamp) {
      const tickTime = Moment(timestamp).tz(this.customerContext.timezone())
      const previousTickTime = Moment(this.previousTickTimestamp).tz(this.customerContext.timezone())
      let timeString = ''

      if (previousTickTime.format('l') === tickTime.format('l')) {
        timeString = tickTime.format('LT')
      } else {
        timeString = `${tickTime.format('LT')} ${tickTime.format('l')}`
      }

      this.previousTickTimestamp = timestamp

      return timeString
    },

    /**
     * Generates a config with the correct color pattern, temperature format,
     * etc.. based on the props passed in
     *
     * @return array
     */
    getConfig () {
      const genericConfig = cloneDeep(this.config)

      const colors = []
      if (get(this, 'data.air.cooler.temps', []).length > 0) {
        colors.push(this.colors.coolerAir)
      }
      if (get(this, 'data.air.freezer.temps', []).length > 0) {
        colors.push(this.colors.freezerAir)
      }

      // Add colors for the simulated product temperatures
      forEach(this.data.products, (product) => {
        colors.push(this.productColors[product.zone])
        // and the forecast (if one is present)
        if (get(product, 'forecast.estimated', []).length > 0) {
          if (toNumber(min(product.forecast.estimated)) < toNumber(product.minTemp) ||
               toNumber(max(product.forecast.estimated)) > toNumber(product.maxTemp)) {
            colors.push(this.colors.danger)
          } else {
            colors.push(this.productColors[product.zone])
          }
        }
      })

      if (get(this, 'data.air.coolerSetPoint.temps', []).length > 0) {
        colors.push(this.colors.coolerSetPoint)
      }

      if (get(this, 'data.air.freezerSetPoint.temps', []).length > 0) {
        colors.push(this.colors.freezerSetPoint)
      }

      const config = {
        padding: {
          bottom: 10
        },
        legend: {
          position: 'bottom',
          item: {
            onclick: (id) => {
              this.$chart.toggle(id)
              $('.' + snakeCase(`${id} Confidence Window`)).toggle()
              this.$emit('legend-click', id)
            }
          }
        },
        color: {
          pattern: colors
        },
        axis: {
          x: {
            type: 'timeseries',
            tick: {
              format: this.formatTick.bind(this),
              multiline: true,
              multilineMax: 2,
              culling: true,
              fit: false
            }
          },
          y: {
            label: {
              text: `${this.$t('temperature')} °${this.customerContext.tempFormat()}`,
              position: 'outer-middle'
            }
          }
        },
        grid: {
          x: {
            lines: []
          },
          y: {
            lines: []
          }
        },
        regions: [],
        point: {
          r: 0,
          select: {
            r: 2
          }
        },
        line: {
          connectNull: true
        },
        tooltip: {
          format: {
            title: function (x) {
              return Moment(x).tz(this.customerContext.timezone()).format('LLL')
            }.bind(this),
            value: function (value, ratio, id) {
              return `${value} &deg;${this.customerContext.tempFormat()}`
            }.bind(this)
          }
        },
        size: {
          height: this.height
        },
        /**
         * I found that the chart would expand to fit the width of the parent
         * element but never resize back down when the window was narrowed. The
         * onresized and onrendered handlers below work together to resize the
         * chart into the correct width by setting the chart's width to 1px when
         * the window is resized (allowing the parent div to resize to it's
         * natural width) and then once rendered if the chart is 1px wide
         * re-size to the parent div's width
         */
        onresized: function () {
          this.handleResize()
        }.bind(this),

        onrendered: function () {
          this.handleRender()

          this.renderConfidenceWindows()
        }.bind(this)
      }

      if (this.data.routeStart) {
        config.grid.x.lines.push({
          value: this.data.routeStart,
          text: this.$t('route_start'),
          position: 'start'
        })
        config.axis.x.min = this.data.routeStart
      }
      if (this.data.routeEnd) {
        config.grid.x.lines.push({
          value: this.data.routeEnd,
          text: this.$t('route_end'),
          position: 'start'
        })
        config.axis.x.max = this.data.routeEnd
      } else if (this.data.maxSimulatedTime) {
        config.grid.x.lines.push({
          value: this.data.maxSimulatedTime,
          text: this.$t('last_update'),
          position: 'start'
        })
        config.axis.x.max = max([this.data.maxForecastTime, Moment().valueOf()])
      }
      if (this.data.dcDeparture) {
        config.grid.x.lines.push({
          value: this.data.dcDeparture,
          text: this.$t('departure'),
          position: 'start'
        })
      }

      forEach(this.data.products, (product, productId) => {
        if (product.minTemp != null) {
          config.grid.y.lines.push({ value: product.minTemp, text: `${product.name} ${this.$t('min')}`, position: 'start' })
        }
        if (product.maxTemp != null) {
          config.grid.y.lines.push({ value: product.maxTemp, text: `${product.name} ${this.$t('max')}`, position: 'start' })
        }
      })

      if (this.showDeliveries) {
        forEach(this.data.deliveries, (delivery) => {
          config.regions.push({
            class: delivery.planned ? 'c3PlannedDelivery' : 'c3UnplannedDelivery',
            start: delivery.start,
            end: delivery.end
          })

          config.grid.x.lines.push({
            value: delivery.start,
            text: delivery.label,
            position: 'middle'
          })
        })
      }

      forEach(this.data.violations, (violation) => {
        // Only show time-gap violations (as in previous Yii implementation)
        if (violation.type === RouteViolationRecord.TYPE_GAP) {
          config.regions.push({
            class: 'c3Violation',
            start: violation.start,
            end: violation.end
          })
        }
      })

      return defaultsDeep(config, genericConfig)
    },

    /**
     * Structures the props into data suitable for the c3 chart
     *
     * @return array
     */
    getData () {
      this.confidenceWindows = {}
      const { type } = this

      const chartData = []
      const chartRegions = []
      const chartClasses = []
      const xs = {}

      if (get(this, 'data.air.cooler.temps', []).length > 0) {
        const coolerLabel = this.$t('cooler_air')
        chartData.push([coolerLabel].concat(this.data.air.cooler.temps))
        chartData.push(['cooler-dates'].concat(this.data.air.cooler.dates))
        xs[coolerLabel] = 'cooler-dates'
      }

      if (get(this, 'data.air.freezer.temps', []).length > 0) {
        const freezerLabel = this.$t('freezer_air')
        chartData.push([freezerLabel].concat(this.data.air.freezer.temps))
        chartData.push(['freezer-dates'].concat(this.data.air.freezer.dates))
        xs[freezerLabel] = 'freezer-dates'
      }

      forEach(this.data.products, (temperatureProduct, key) => {
        // Add in the simulated product temperature data
        const productName = temperatureProduct.name
        let dateKey = snakeCase(productName) + '_dates'
        chartData.push([productName].concat(temperatureProduct.simulated.temps))
        chartData.push([dateKey].concat(temperatureProduct.simulated.dates))
        xs[productName] = dateKey

        // Add in the forecast information (if present)
        if (get(temperatureProduct, 'forecast.estimated', []).length > 0) {
          const productNameForecast = `${productName} (Forecast)`
          dateKey = snakeCase(productNameForecast) + '_dates'
          chartData.push([productNameForecast].concat(temperatureProduct.forecast.estimated))
          chartData.push([dateKey].concat(temperatureProduct.forecast.dates))
          xs[productNameForecast] = dateKey
          chartClasses[productNameForecast] = ['forecast']

          // C3 doesn't provide a native way to add chart areas so at this point
          // just add the confidence window data to this.confidenceWindows and
          // we'll render it in this.renderConfidenceWindows()
          const confidenceWindowName = `${productName}_forecast_window`
          this.confidenceWindows[confidenceWindowName] = {
            product: temperatureProduct,
            x: temperatureProduct.forecast.dates,
            low: temperatureProduct.forecast.low,
            high: temperatureProduct.forecast.high,
            minTemp: temperatureProduct.minTemp,
            maxTemp: temperatureProduct.maxTemp
          }
        }
      })

      if (get(this, 'data.air.coolerSetPoint.temps', []).length > 0) {
        const label = this.$t('cooler_setpoint')
        chartData.push([label].concat(this.data.air.coolerSetPoint.temps))
        chartData.push(['cooler-setpoint-dates'].concat(this.data.air.coolerSetPoint.dates))
        xs[label] = 'cooler-setpoint-dates'
      }

      if (get(this, 'data.air.freezerSetPoint.temps', []).length > 0) {
        const label = this.$t('freezer_setpoint')
        chartData.push([label].concat(this.data.air.freezerSetPoint.temps))
        chartData.push(['freezer-setpoint-dates'].concat(this.data.air.freezerSetPoint.dates))
        xs[label] = 'freezer-setpoint-dates'
      }

      // Present generic data (not attached to any specific thing)
      forEach(this.data.other, other => {
        const label = other.name
        const dateKey = snakeCase(label) + '_dates'
        chartData.push([label].concat(other.temps))
        chartData.push([dateKey].concat(other.dates))
        xs[label] = dateKey
      })

      const data = {
        type: 'line',
        xs: xs,
        columns: chartData,
        regions: chartRegions,
        classes: chartClasses
      }

      return defaultsDeep({ type }, data)
    },

    /**
     * Render the confidence windows once the c3 chart has been initialized
     *
     * @return undefined
     */
    afterInitHook () {
      // Hide every legend column that has its visibility explicitly set to false.
      forEach(this.initialColumnVisibility, (value, key) => {
        if (value === false) {
          this.$chart.hide(key)
        }
      })
      // The chart doesn't appear to render when opened in a new (but not yet
      // visible) tab. This bit of code ensures that when the tab eventually has
      // focus the chart is rendered (this is called only once and then
      // unregistered - as indicated by the 'once' option). We also remove it
      // in beforeDestroy (VF-3516) to avoid firing against a destroyed chart.
      this.$_visibilityChangeHandler = () => {
        if (this.$chart && typeof this.$chart.resize === 'function') {
          this.$chart.resize({
            height: this.height,
            width: 1
          })
        }
      }
      document.addEventListener(
        'visibilitychange',
        this.$_visibilityChangeHandler,
        { once: true }
      )

      this.renderConfidenceWindows()
    },

    /**
     * Renders confidence window areas on to the c3 chart using the data defined
     * in this.confidenceWindows
     *
     * @return undefined
     */
    renderConfidenceWindows () {
      // This method is called by c3's onrendered handler which will execute
      // before the $chart property is set on this instance. For this reason
      // make sure the chart internal property is defined before proceeding
      if (get(this, '$chart.internal') == null) {
        return
      }

      // pull the x and y internal methods to reshape the data to fit the layout
      // of the chart.
      const yscale = this.$chart.internal.y
      const xscale = this.$chart.internal.x

      forEach(this.confidenceWindows, (window) => {
        const windowProductName = snakeCase(`${window.product.name} (Forecast) Confidence Window`)

        const indices = d3.range(window.x.length)

        const area = d3.area()
          .x((d) => {
            return xscale(window.x[d])
          })
          .y0((d) => {
            return yscale(window.low[d])
          })
          .y1((d) => {
            return yscale(window.high[d])
          })

        // Pull and update the existing element or if it doesn't exist then
        // create a new one.
        let elem = d3.select(`.chart-root svg g .confidence-window.${windowProductName}`)

        if (elem.empty()) {
          elem = d3.select('.chart-root svg g')
            .insert('path', '.c3-chart')
            .datum(indices)

          // Determine if the window breaches the product min/max and set the
          // class on the element accordingly
          if (min(window.product.forecast.low) < window.product.minTemp ||
               max(window.product.forecast.high) > window.product.maxTemp) {
            elem.attr('class', `confidence-window ${windowProductName} danger`)
          } else {
            elem.attr('class', `confidence-window ${windowProductName} ${window.product.zone}`)
          }
        }

        // Finally set the data on the element (regardless of whether it's newly
        // created or already exists)
        elem.attr('d', area)
      })
    }
  },
  beforeDestroy () {
    if (this.$_visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.$_visibilityChangeHandler
      )
      this.$_visibilityChangeHandler = null
    }
  },
  watch: {
    /**
     * Re-render the chart if the user toggles the show deliveries switch
     */
    showDeliveries () {
      this.regenerate()
    }
  }
}
</script>

<style lang="scss">
    @import "src/assets/scss/colors.scss";

    .c3-region.c3Violation {
        fill: #B33505;
    }

    /** matches the colors for planned and unplanned deliveries in
     * RouteLocationsMap.vue */
    .c3-region.c3PlannedDelivery {
        fill: #6991fd;
    }

    /** matches the colors for planned and unplanned deliveries in
     * RouteLocationsMap.vue */
    .c3-region.c3UnplannedDelivery {
        fill: #17a2b8;
    }

    svg {
      path.confidence-window {
        stroke: unset
      }

      path.confidence-window.freezer {
        fill: #44afed;
        fill-opacity: .3;
      }

      path.confidence-window.cooler {
        fill: #29de29;
        fill-opacity: .3;
      }

      path.confidence-window.danger {
        fill: #B33505;
        fill-opacity: .3;
      }
    }

    .c3-target-forecast {
      stroke-dasharray:1%, .5%
    }

</style>
